import { useEffect, useState } from 'react';
import { fetchPublishedBidsForAccount } from '../services/alaCarteBidService';

/**
 * Published à la carte bid prices for one account, keyed by catalog item id.
 *
 * A case-by-case deliverable carries no catalog price — it only has a number once a bid for it
 * has been published to that specific lead/client. Program builders use this to decide whether a
 * case-by-case line is priced (counts toward the monthly total) or still "to be priced" (shows in
 * the "+ N" count and renders gold).
 *
 * A failed load resolves to an empty map, so every case-by-case line simply reads as unpriced
 * rather than breaking the builder.
 *
 * @param {string} accountId
 * @returns {Record<string, number>} catalog item id → published clientListPrice
 */
export function usePublishedBidPrices(accountId) {
  const [bidPricesByItemId, setBidPricesByItemId] = useState({});

  useEffect(() => {
    if (!accountId) {
      setBidPricesByItemId({});
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const bids = await fetchPublishedBidsForAccount(accountId);
        if (cancelled) return;
        const prices = {};
        bids.forEach((bid) => {
          const price = parseFloat(bid?.clientListPrice);
          if (bid?.catalogItemId && Number.isFinite(price) && price > 0) {
            prices[bid.catalogItemId] = price;
          }
        });
        setBidPricesByItemId(prices);
      } catch (err) {
        if (!cancelled) console.error('Error fetching published bids:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [accountId]);

  return bidPricesByItemId;
}

export default usePublishedBidPrices;
